import { MAX_DOCUMENTS, MAX_LINKS, type UseWidgetEmbedResult } from '@/hooks/use-widget-embed';
import { EMBED_CSS, styles } from './widget-form.styles';

interface WidgetFormProps {
  embed: UseWidgetEmbedResult;
}

export function WidgetForm({ embed }: WidgetFormProps) {
  if (embed.status === 'loading') {
    return <div style={styles.loading}>Loading widget...</div>;
  }

  if (embed.status === 'error') {
    return <div style={styles.error}>{embed.message || 'Widget is unavailable.'}</div>;
  }

  if (embed.status === 'success') {
    return (
      <div style={styles.successWrap}>
        <h2 style={styles.successTitle}>Claim Received</h2>
        <p style={styles.successText}>A team member will review your claim request and follow up shortly.</p>
        {embed.submitResult?.claimNumber && (
          <p style={styles.successText}>Reference number: <strong>{embed.submitResult.claimNumber}</strong></p>
        )}
      </div>
    );
  }

  return (
    <div className="tc-embed" style={styles.shell}>
      <style dangerouslySetInnerHTML={{ __html: EMBED_CSS }} />
      <div style={styles.card}>
        <form onSubmit={embed.handleSubmit} style={styles.form}>
          <div className="tc-grid-2">
            <label style={styles.label}>
              <span>First Name <span style={styles.requiredMark}>*</span></span>
              <input required type="text" value={embed.formState.homeownerFirstName} onChange={embed.onFieldChange('homeownerFirstName')} style={styles.input} maxLength={120} />
            </label>

            <label style={styles.label}>
              <span>Last Name <span style={styles.requiredMark}>*</span></span>
              <input required type="text" value={embed.formState.homeownerLastName} onChange={embed.onFieldChange('homeownerLastName')} style={styles.input} maxLength={120} />
            </label>

            <label style={styles.label}>
              <span>Email <span style={styles.requiredMark}>*</span></span>
              <input required type="email" value={embed.formState.homeownerEmail} onChange={embed.onFieldChange('homeownerEmail')} style={styles.input} maxLength={320} />
            </label>

            <label style={styles.label}>
              <span>Phone <span style={styles.requiredMark}>*</span></span>
              <input required type="tel" value={embed.formState.homeownerPhone} onChange={embed.onFieldChange('homeownerPhone')} style={styles.input} maxLength={50} />
            </label>

            <label style={styles.label}>
              <span>Street Address <span style={styles.requiredMark}>*</span></span>
              <input
                required
                type="text"
                value={embed.formState.propertyStreetAddress}
                onChange={embed.onFieldChange('propertyStreetAddress')}
                style={styles.input}
                placeholder="123 Main St"
                maxLength={255}
              />
            </label>

            <label style={styles.label}>
              <span>City <span style={styles.requiredMark}>*</span></span>
              <input
                required
                type="text"
                value={embed.formState.propertyCity}
                onChange={embed.onFieldChange('propertyCity')}
                style={styles.input}
                placeholder="Dallas"
                maxLength={255}
              />
            </label>

            <label style={styles.label}>
              <span>State <span style={styles.requiredMark}>*</span></span>
              <input
                required
                type="text"
                value={embed.formState.propertyState}
                onChange={embed.onFieldChange('propertyState')}
                style={styles.input}
                placeholder="TX"
                maxLength={2}
                pattern="[A-Za-z]{2}"
                title="Use 2-letter state code"
              />
            </label>

            <label style={styles.label}>
              <span>ZIP Code <span style={styles.requiredMark}>*</span></span>
              <input
                required
                type="text"
                value={embed.formState.propertyZipCode}
                onChange={embed.onFieldChange('propertyZipCode')}
                style={styles.input}
                placeholder="75201"
                inputMode="numeric"
                maxLength={10}
                pattern="[0-9]{5}(-[0-9]{4})?"
                title="Use 5 digits or ZIP+4"
              />
            </label>

            <label style={styles.label}>
              <span>Date of Loss</span>
              <input type="date" value={embed.formState.dateOfLoss} onChange={embed.onFieldChange('dateOfLoss')} style={styles.input} />
            </label>

            <label style={styles.label}>
              <span>Cause of Loss</span>
              <input type="text" value={embed.formState.causeOfLoss} onChange={embed.onFieldChange('causeOfLoss')} style={styles.input} maxLength={100} />
            </label>

            <label style={styles.label}>
              <span>Insurance Company</span>
              <input type="text" value={embed.formState.insuranceCompany} onChange={embed.onFieldChange('insuranceCompany')} style={styles.input} maxLength={255} />
            </label>

            <label style={styles.label}>
              <span>Policy Number</span>
              <input type="text" value={embed.formState.policyNumber} onChange={embed.onFieldChange('policyNumber')} style={styles.input} maxLength={100} />
            </label>

            <label style={styles.label}>
              <span>Contractor Company</span>
              <input type="text" value={embed.formState.contractor} onChange={embed.onFieldChange('contractor')} style={styles.input} maxLength={255} />
            </label>

            <label style={styles.label}>
              <span>Contractor Name</span>
              <input type="text" value={embed.formState.contractorContact} onChange={embed.onFieldChange('contractorContact')} style={styles.input} maxLength={255} />
            </label>

            <label style={styles.label}>
              <span>Contractor Email</span>
              <input type="email" value={embed.formState.contractorEmail} onChange={embed.onFieldChange('contractorEmail')} style={styles.input} maxLength={320} />
            </label>

            <label style={styles.label}>
              <span>Contractor Phone</span>
              <input type="tel" value={embed.formState.contractorPhone} onChange={embed.onFieldChange('contractorPhone')} style={styles.input} maxLength={50} />
            </label>
          </div>

          <label style={styles.label}>
            Description
            <textarea rows={5} value={embed.formState.description} onChange={embed.onFieldChange('description')} style={styles.textarea} maxLength={4000} />
          </label>

          <div style={styles.linkSection}>
            <div style={styles.linkHeaderRow}>
              <div style={styles.linkHeaderContent}>
                <span style={styles.linkTitle}>Reference links</span>
                <p style={styles.linkHint}>Optional. Add supporting URLs like estimates, photo albums, or claim docs.</p>
              </div>
              <button type="button" onClick={embed.addLink} style={styles.linkAddButton} disabled={embed.links.length >= MAX_LINKS}>Add link</button>
            </div>
            {embed.links.map((link, index) => (
              <div key={`link-${index}`} style={styles.linkRow}>
                <span style={styles.linkIndex}>{index + 1}</span>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={link.url}
                  onChange={(event) => embed.updateLink(index, { url: event.target.value })}
                  style={{ ...styles.input, ...styles.linkInput }}
                />
                <button type="button" onClick={() => embed.removeLink(index)} style={styles.linkRemoveButton}>Remove</button>
              </div>
            ))}
          </div>

          <label style={styles.label}>
            Upload supporting documents
            <input type="file" multiple onChange={embed.handleFileChange} style={styles.input} />
          </label>

          {embed.documents.length > 0 && (
            <div style={styles.documentSection}>
              <div style={styles.documentCount}>{embed.documents.length} of {MAX_DOCUMENTS} documents selected</div>
              <ul style={styles.documentList}>
                {embed.documents.map((file, index) => (
                  <li key={`${file.name}-${index}`} style={styles.documentItemRow}>
                    <span style={styles.documentItemName}>{file.name}</span>
                    <button type="button" onClick={() => embed.removeDocument(index)} style={styles.documentRemoveButton}>Remove</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <input
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={styles.honeypot}
            value={embed.formState.companyWebsite}
            onChange={embed.onFieldChange('companyWebsite')}
            name="companyWebsite"
          />

          {embed.config?.captcha && <div id="turnstile-container" />}

          {embed.message && <div style={styles.inlineError}>{embed.message}</div>}

          <button type="submit" disabled={embed.status === 'submitting' || embed.isCaptchaMissing} style={styles.submitButton}>
            {embed.status === 'submitting' ? 'Submitting...' : 'Submit Claim'}
          </button>
        </form>
      </div>
    </div>
  );
}

